import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { getIndexPage, isCollection, isIndex } from '../utils.ts';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { ComponentLike } from '@glint/template';
type InternalPageYield = {
    page: Page;
    Link: ComponentLike<{
        Element: HTMLAnchorElement;
        Blocks: {
            default: [page: Page, isActive: boolean];
        };
    }>;
};
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name and element.
     * By default the `name` property will be used in a link.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       <x.Link>
     *         {{toSentenceCase page.name}}
     *       </x.Link>
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [{
                page: Page;
                Link: ComponentLike<{
                    Element: HTMLAnchorElement;
                    Blocks: {
                        default: [page: Page, isActive: boolean];
                    };
                }>;
            }];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used or a link will be rendered if an index page is present..
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |x|>
     *       {{#if x.index}}
     *         <x.index.Link>
     *           {{sentenceCase x.collection.name}}
     *         </x.index.Link>
     *       {{else}}
     *         {{sentenceCase x.collection.name}}
     *       {{/if}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [{
                collection: Collection;
                /**
         * If there is an index page, it'll be provided here,
         * and omitted from the :page block.
         */ index?: {
                    page: Page;
                    Link: ComponentLike<{
                        Element: HTMLAnchorElement;
                        Blocks: {
                            default: [page: Page, isActive: boolean];
                        };
                    }>;
                };
            }];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            <p.Link>
              {{p.page.name}}
            </p.Link>
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{#if c.index}}
              <c.index.Link>
                {{c.index.page.name}}
              </c.index.Link>
            {{else}}
              {{c.collection.name}}
            {{/if}}
          {{/if}}
        </:collection>
      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
const not = (x1: unknown)=>!x1;
const Pages: TOC<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [InternalPageYield];
        collection: [{
                collection: Collection;
                index?: InternalPageYield;
            }];
    };
}> = template(`
  {{#if (isCollection @item)}}
    <ul>
      {{#each @item.pages as |page|}}
        {{#if (not (isIndex page))}}
          <li>
            {{#if (isCollection page)}}

              {{! index.md pages can make the whole collection clickable }}
              {{#let (getIndexPage page) as |indexPage|}}
                {{#if indexPage}}
                  {{yield
                    (hash
                      collection=page
                      index=(hash
                        page=indexPage
                        Link=(component
                          PageLink item=indexPage activeClass=@activeClass
                        )
                      )
                    )
                    to='collection'
                  }}
                {{else}}
                  {{yield (hash collection=page) to='collection'}}
                {{/if}}
              {{/let}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/if}}
      {{/each}}
    </ul>
  {{else}}
    {{yield
      (hash
        page=@item Link=(component PageLink item=@item activeClass=@activeClass)
      )
      to='page'
    }}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
let PageLink = class PageLink extends Component<{
    Element: HTMLAnchorElement;
    Args: {
        item: Page;
        activeClass?: string;
    };
    Blocks: {
        default: [page: Page, isActive: boolean];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    get isActive() {
        let subPath1 = this.args.item.path;
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1) ?? false;
    }
    static{
        template(`
    <a
      href={{@item.path}}
      class={{if this.isActive this.activeClass}}
      ...attributes
    >{{yield @item this.isActive}}</a>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,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